import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { getIndexPage, isCollection, isIndex } from '../utils.ts';
import type DocsService from '../services/kolay/docs.ts';
import type { Collection, Page } from '../services/kolay/types.ts';
import type { TOC } from '@ember/component/template-only';
import type RouterService from '@ember/routing/router-service';
import type { ComponentLike } from '@glint/template';
type InternalPageYield = {
    page: Page;
    Link: ComponentLike<{
        Element: HTMLAnchorElement;
        Blocks: {
            default: [page: Page, isActive: boolean];
        };
    }>;
};
export class PageNav extends Component<{
    /**
   * The `<nav>` element. It has a default `aria-label` of "Selected Group".
   * Normally an `aria-label` is not required,
   * but when there are multiple `<nav>` elements on a screen, it is required.
   */ Element: HTMLElement;
    Args: {
        /**
     * The class to apply to the `<a>` element when its link is active.
     *
     * Defaults to "active"
     */ activeClass?: string;
    };
    Blocks: {
        /**
     * If provided, this block will yield back the page for customizing the name and element.
     * By default the `name` property will be used in a link.
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:page as |page|>
     *       <x.Link>
     *         {{toSentenceCase page.name}}
     *       </x.Link>
     *     </:page>
     *   </PageNav>
     * </template>
     * ```
     */ page: [{
                page: Page;
                Link: ComponentLike<{
                    Element: HTMLAnchorElement;
                    Blocks: {
                        default: [page: Page, isActive: boolean];
                    };
                }>;
            }];
        /**
     * If provided, this block will yield back the collection for customizing the name.
     * By default the `name` property will be used or a link will be rendered if an index page is present..
     *
     * Example:
     * ```gjs
     * import { PageNav } from 'kolay/components';
     *
     * function toSentenceCase(name) { /* ... *\/ }
     *
     * <template>
     *   <PageNav>
     *     <:collection as |x|>
     *       {{#if x.index}}
     *         <x.index.Link>
     *           {{sentenceCase x.collection.name}}
     *         </x.index.Link>
     *       {{else}}
     *         {{sentenceCase x.collection.name}}
     *       {{/if}}
     *     </:collection>
     *   </PageNav>
     * </template>
     * ```
     */ collection: [{
                collection: Collection;
                /**
         * If there is an index page, it'll be provided here,
         * and omitted from the :page block.
         */ index?: {
                    page: Page;
                    Link: ComponentLike<{
                        Element: HTMLAnchorElement;
                        Blocks: {
                            default: [page: Page, isActive: boolean];
                        };
                    }>;
                };
            }];
    };
}> {
    @service('kolay/docs')
    docs: DocsService;
    /**
   * Ember doesn't yet have a way to forward blocks,
   * so we have  to do this weird manualy forwarding ourselves
   *
   * This is extra annoying since Pages is a recursive component.
   */ static{
        template(`
    <nav aria-label='Selected Group' ...attributes>
      <Pages @item={{this.docs.tree}}>

        <:page as |p|>
          {{#if (has-block 'page')}}
            {{yield p to='page'}}
          {{else}}
            <p.Link>
              {{p.page.name}}
            </p.Link>
          {{/if}}
        </:page>

        <:collection as |c|>
          {{#if (has-block 'collection')}}
            {{yield c to='collection'}}
          {{else}}
            {{#if c.index}}
              <c.index.Link>
                {{c.index.page.name}}
              </c.index.Link>
            {{else}}
              {{c.collection.name}}
            {{/if}}
          {{/if}}
        </:collection>
      </Pages>
    </nav>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
const not = (x1: unknown)=>!x1;
const Pages: TOC<{
    Args: {
        item: Page | Collection;
        activeClass?: string;
    };
    Blocks: {
        page: [InternalPageYield];
        collection: [{
                collection: Collection;
                index?: InternalPageYield;
            }];
    };
}> = template(`
  {{#if (isCollection @item)}}
    <ul>
      {{#each @item.pages as |page|}}
        {{#if (not (isIndex page))}}
          <li>
            {{#if (isCollection page)}}

              {{! index.md pages can make the whole collection clickable }}
              {{#let (getIndexPage page) as |indexPage|}}
                {{#if indexPage}}
                  {{yield
                    (hash
                      collection=page
                      index=(hash
                        page=indexPage
                        Link=(component
                          PageLink item=indexPage activeClass=@activeClass
                        )
                      )
                    )
                    to='collection'
                  }}
                {{else}}
                  {{yield (hash collection=page) to='collection'}}
                {{/if}}
              {{/let}}
            {{/if}}

            <Pages @item={{page}}>
              <:page as |p|>{{yield p to='page'}}</:page>
              <:collection as |c|>{{yield c to='collection'}}</:collection>
            </Pages>
          </li>
        {{/if}}
      {{/each}}
    </ul>
  {{else}}
    {{yield
      (hash
        page=@item Link=(component PageLink item=@item activeClass=@activeClass)
      )
      to='page'
    }}
  {{/if}}
`, {
    eval () {
        return eval(arguments[0]);
    }
});
let PageLink = class PageLink extends Component<{
    Element: HTMLAnchorElement;
    Args: {
        item: Page;
        activeClass?: string;
    };
    Blocks: {
        default: [page: Page, isActive: boolean];
    };
}> {
    @service
    router: RouterService;
    get activeClass() {
        return this.args.activeClass ?? 'active';
    }
    get isActive() {
        let subPath1 = this.args.item.path;
        if (subPath1 === '/') return false;
        return this.router.currentURL?.startsWith(subPath1) ?? false;
    }
    static{
        template(`
    <a
      href={{@item.path}}
      class={{if this.isActive this.activeClass}}
      ...attributes
    >{{yield @item this.isActive}}</a>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
};
//# sourceMappingURL=data:application/json;base64,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